import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { isNativeShell } from '@mlm/ui/lib/native-shell';
import type { MyCreatorSubscription } from '@mlm/shared';
import { CoachButton, Eyebrow } from './primitives';
import { CoursePurchase } from './course-purchase';
import { fmtPeso, learn } from '../lib/api';
import { instantDay } from '../lib/format';
import { qk } from '../lib/queries';

/**
 * The member's creator subscriptions on the Learn tab: what each one gives
 * until when, and how it renews. On the web a card subscription is managed in
 * Stripe's billing page and a lapsing one can be renewed here; inside the
 * phone app both stay out (nothing digital is sold or managed there).
 */
export function MySubscriptions() {
  const native = isNativeShell();
  const q = useQuery({
    queryKey: qk.mySubscriptions,
    queryFn: learn.subscriptions,
    staleTime: 60_000,
  });
  const portal = useMutation({
    mutationFn: learn.billingPortal,
    onSuccess: ({ url }) => window.location.assign(url),
  });
  if (!q.data || q.data.length === 0) return null;
  return (
    <section className="border-b border-border px-5 py-5">
      <Eyebrow className="mb-3">Subscriptions</Eyebrow>
      <ul className="space-y-4">
        {q.data.map((s) => (
          <SubscriptionRow
            key={s.creatorUserId}
            sub={s}
            native={native}
            onManage={() => portal.mutate()}
            managing={portal.isPending}
          />
        ))}
      </ul>
    </section>
  );
}

function statusLine(s: MyCreatorSubscription): string {
  const until = instantDay(s.expiresAt);
  const card = s.card;
  if (card) {
    if (card.payUrl) return 'Your last card payment did not go through.';
    if (card.cancelScheduled) return `Ends ${until}. Card renewal is off.`;
    if (card.renewsAt)
      return `Renews by card on ${instantDay(card.renewsAt)}, ${fmtPeso(card.price)}.`;
  }
  const wallet = s.autoRenew;
  if (wallet?.status === 'past_due') return 'Renewal is retrying: your wallet was short.';
  if (wallet?.status === 'active' && wallet.funding !== 'stripe') {
    return `Renews from your wallet on ${until}.`;
  }
  return s.active ? `Access until ${until}.` : `Ended ${until}.`;
}

function SubscriptionRow({
  sub,
  native,
  onManage,
  managing,
}: {
  sub: MyCreatorSubscription;
  native: boolean;
  onManage: () => void;
  managing: boolean;
}) {
  const [renewing, setRenewing] = useState(false);
  const card = sub.card;
  const cardRenews = !!card && !!card.renewsAt && !card.cancelScheduled;
  const canRenew = !native && !!sub.renewPlan && !cardRenews;
  return (
    <li className="border border-border px-4 py-4">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <p className="text-[0.95rem] text-ink">{sub.creatorName}</p>
        <Eyebrow>{sub.periodLabel}</Eyebrow>
      </div>
      <p className="mt-1 text-[0.88rem] text-ink/70">{statusLine(sub)}</p>
      {!native && (card?.manageable || card?.payUrl || canRenew) && (
        <div className="mt-3 flex flex-wrap gap-2">
          {card?.payUrl && (
            <a href={card.payUrl}>
              <CoachButton kind="gold">Pay now</CoachButton>
            </a>
          )}
          {card?.manageable && (
            <CoachButton kind="outline" disabled={managing} onClick={onManage}>
              {managing ? 'Opening…' : 'Manage billing'}
            </CoachButton>
          )}
          {canRenew && !renewing && (
            <CoachButton kind="outline" onClick={() => setRenewing(true)}>
              Renew
            </CoachButton>
          )}
        </div>
      )}
      {renewing && sub.renewPlan && (
        <div className="mt-4">
          <CoursePurchase
            mode="subscribe"
            seller={{ creatorUserId: sub.creatorUserId, isHouse: sub.creatorIsHouse }}
            options={[sub.renewPlan]}
          />
        </div>
      )}
    </li>
  );
}
